import { Component, Inject, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { Utils } from '../../../../../../utils/utils';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { RouterModule } from '@angular/router';

interface MGMTHierarchyFormData {
    mgmtId: string;
    mgmtName: string;
    groupFunction: string[];
}

@Component({
  selector: 'app-editgroupfunctionsdialogue',
  standalone: true,
  imports: [
    MatDialogModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatToolbarModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule
  ],
  templateUrl: './editgroupfunctionsdialogue.component.html',
    styleUrls: ['./editgroupfunctionsdialogue.component.less',
        '../../../../../../style/new-generic-styles.component.less']
})
export class Editgroupfunctionsdialogue implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { availableGroupFunctions: any, mgmtGroupFunctionsInfo: MGMTHierarchyRefNode[], isReadOnly?: boolean },
        public dialogRef: MatDialogRef<Editgroupfunctionsdialogue>,
        private readonly _formBuilder: FormBuilder,
        private readonly http: HttpClient,
        @Inject('BASE_URL') private readonly baseUrl: string) { }

    columnsToDisplay = ["mgmtId", "mgmtName", "groupFunction"];

    GFMgmtForm!: FormGroup;
    public dataSource = new MatTableDataSource<any>();
    public searchString: string = '';
    public subscriptions: Subscription[] = [];
    private originalData: MGMTHierarchyFormData[] = [];
    private hasChanges: boolean = false;
    private _changedDataCache: any[] | null = null;

    ngOnInit(): void {
        this.GFMgmtForm = this._formBuilder.group({
            GFMgmtRows: this._formBuilder.array(this.data.mgmtGroupFunctionsInfo.map((val, index) => this._formBuilder.group({
                mgmtId: new FormControl(val.mgmtId),
                mgmtName: new FormControl(val.mgmtName),
                groupFunction: new FormControl(this.parseGroupFunction(val.groupFunction)),               
            })
            )) 
        });
        this.searchString = '';
        // Deep copy to avoid shared references
        this.originalData = structuredClone((this.GFMgmtForm.get('GFMgmtRows') as FormArray).value);
        this.dataSource.data = (this.GFMgmtForm.get('GFMgmtRows') as FormArray).value;
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    onBack() {
        this.dialogRef.close();

    }

    onDownload() {
        Utils.httpDownloadMgmtHierarchyRefGroupFunctions(this.http, this.baseUrl);
    }

    onSelectionChange(event: any, mgmtId: string) {

        // Get the new value directly from the event
        const newSelection = event.value as string[];      

        if (newSelection.length === 0) {
            // Restore initial backend value if user tries to unselect the last one
            const original = this.originalData.find(item => item.mgmtId === mgmtId)?.groupFunction;

            if (original) {
                // Update the FormArray
                const configRows = this.GFMgmtForm.get('GFMgmtRows') as FormArray;
                const formGroupIndex = (configRows.value as MGMTHierarchyFormData[])
                    .findIndex(item => item.mgmtId === mgmtId);

                if (formGroupIndex !== -1) {
                    const formGroup = configRows.at(formGroupIndex);
                    const gfControl = formGroup.get('groupFunction');
                    if (gfControl) {
                        gfControl.setValue(original, { emitEvent: false });
                    }
                }

                // Also update the dataSource element to reflect in the UI
                const dataSourceItem = this.dataSource.data.find(item => item.mgmtId === mgmtId);
                dataSourceItem.groupFunction = [...original];
            }
            return;
        }

        // Update the FormArray with the new selection
        const configRows = this.GFMgmtForm.get('GFMgmtRows') as FormArray;
        const formGroupIndex = (configRows.value as MGMTHierarchyFormData[])
            .findIndex(item => item.mgmtId === mgmtId);

        if (formGroupIndex !== -1) {
            const formGroup = configRows.at(formGroupIndex);
            const gfControl = formGroup.get('groupFunction');
            if (gfControl) {
                gfControl.setValue(newSelection, { emitEvent: false });
            }
        }

        // Mark that changes occurred and invalidate cache
        this.hasChanges = true;
        this._changedDataCache = null;
    }

    getSelectorText(groupFunctions: any) {

        return this.data.availableGroupFunctions
            .filter(gf => groupFunctions.includes(gf))
                 .join('/') || '';
    }

    onSave() {

        const changedData = this.getChangedData();

        if (changedData.length === 0) {
            this.dialogRef.close({
                success: true,
                message: 'No changes to save'
            });
            return;
        }

        // Convert arrays back to strings for backend
        const dataBody = changedData.map(item => ({
            mgmtId: item.mgmtId,
            mgmtName: item.mgmtName,
            groupFunction: this.serializeGroupFunction(item.groupFunction)
        }));

        Utils.httpUpdateMgmtBranchHierarchyGroupFunctions(
            this.http,
            this.baseUrl,
            dataBody,
            this,
            function (tthis: Editgroupfunctionsdialogue, result: any) {
                tthis.dialogRef.close({
                    success: result,
                });
            },
            function (tthis: Editgroupfunctionsdialogue, error: any) {
                tthis.dialogRef.close({
                    success: false,
                    errorMssg: 'An unexpected error occurred while saving management hierarchy group functions'
                });
            }
        )
    }

    anyChange(): boolean {
        // Use fast flag first
        if (!this.hasChanges) {
            return false;
        }

        // Use cache if available
        if (this._changedDataCache !== null) {
            return this._changedDataCache.length > 0;
        }

        // If no cache, calculate and cache
        this._changedDataCache = this.getChangedDataInternal();
        return this._changedDataCache.length > 0;
    }

    async updateFilteredData() {
        this.dataSource.data = this.filterAndShowResults(this.searchString);
    }

    // Parse string to array
    private parseGroupFunction(groupFunction: string | null | undefined): string[] {
        if (!groupFunction) {
            return [];
        }
        return groupFunction.split('/').map(gf => gf.trim()).filter(gf => gf.length > 0);
    }

    // Convert array to string for backend
    private serializeGroupFunction(groupFunctions: string[]): string {
        if (!groupFunctions || groupFunctions.length === 0) {
            return '';
        }
        return groupFunctions.join('/');
    }

    private getChangedData(): any[] {
        // Invalidate cache and get fresh data
        this._changedDataCache = null;
        return this.getChangedDataInternal();
    }

    private getChangedDataInternal(): MGMTHierarchyFormData[] {
        const changedItems: MGMTHierarchyFormData[] = [];
        const currentData = (this.GFMgmtForm.get('GFMgmtRows') as FormArray).value as MGMTHierarchyFormData[];

        currentData.forEach((current: MGMTHierarchyFormData) => {
            // Find original element by mgmtId (not by index, since filter can change order)
            const original = this.originalData.find(item => item.mgmtId === current.mgmtId);

            if (!original) {
                return;
            }

            // Compare groupFunction
            if (this.hasGroupFunctionChanged(current.groupFunction, original.groupFunction)) {
                changedItems.push(current);
            }
        });

        // For changedItems 

        return changedItems;
    }

    private hasGroupFunctionChanged(current: string[], original: string[]): boolean {
        // Both should be arrays
        if (!Array.isArray(current) || !Array.isArray(original)) {
            return true;
        }

        // Check if they have the same length and elements
        if (current.length !== original.length) {
            return true;
        }

        // Sort for consistent comparison
        const sortedCurrent = [...current].sort((a, b) => a.localeCompare(b));
        const sortedOriginal = [...original].sort((a, b) => a.localeCompare(b));
        return !sortedCurrent.every((val: string, idx: number) => val === sortedOriginal[idx]);
    }

    private filterAndShowResults(searchString: string): any[] {
        // Get ALL current data from FormArray
        const allData = (this.GFMgmtForm.get('GFMgmtRows') as FormArray).value;

        // If no search, show all
        if (searchString === "") {
            return allData;
        }

        // Filter for display only
        return allData.filter((item: any) =>
            item.mgmtId.toLowerCase().includes(searchString.toLowerCase()) ||
            item.mgmtName.toLowerCase().includes(searchString.toLowerCase())
        );
    }
}
